/**
 * Lightweight confetti burst for the level-clear overlay.
 *
 * Pure Pixi Graphics rectangles + simple Euler integration. No texture
 * atlas, no shader; ~150 particles is well under 1ms per frame. Particles
 * have gravity, drag, angular velocity, and a fade-to-zero alpha so the
 * burst self-cleans without explicit lifetime tracking elsewhere.
 */

import { Container, Graphics } from 'pixi.js';

interface Piece {
  g: Graphics;
  vx: number;
  vy: number;
  vrot: number;
  age: number;
  life: number;
}

const COLORS = [
  0xff5577, 0xffcc33, 0x55ff77, 0x33ccff, 0xaa77ff,
  0xff8833, 0xffffff, 0xff66cc, 0x77ff44, 0x44ddff,
];

export class Confetti {
  /** Container the user adds to their stage; pieces are created inside. */
  readonly view: Container = new Container();
  private pieces: Piece[] = [];

  /** Spawn a burst centered on (cx, cy) screen coordinates. */
  burst(cx: number, cy: number, count = 140): void {
    for (let i = 0; i < count; i++) {
      const g = new Graphics();
      const w = 6 + Math.random() * 6;
      const h = 3 + Math.random() * 4;
      const color = COLORS[(Math.random() * COLORS.length) | 0]!;
      g.rect(-w / 2, -h / 2, w, h).fill(color);
      g.x = cx;
      g.y = cy;
      // Aim mostly upward in a wide cone so the burst arcs out and falls.
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
      const speed = 6 + Math.random() * 9;
      const piece: Piece = {
        g,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        vrot: (Math.random() - 0.5) * 0.4,
        age: 0,
        life: 1.6 + Math.random() * 1.2, // seconds before fully faded
      };
      g.rotation = Math.random() * Math.PI * 2;
      this.view.addChild(g);
      this.pieces.push(piece);
    }
  }

  /** Advance the simulation. dt in seconds. Removes faded pieces. */
  update(dt: number): void {
    if (this.pieces.length === 0) return;
    const GRAVITY = 22; // px/s² in screen-space units (tuned to feel snappy)
    const DRAG = 0.985;
    const kept: Piece[] = [];
    for (const p of this.pieces) {
      // Defensive: if the Graphics was destroyed externally (e.g. its
      // parent container was wiped via destroy({children:true})) drop the
      // piece silently rather than crashing the ticker.
      if (p.g.destroyed) continue;
      p.age += dt;
      const t = p.age / p.life;
      if (t >= 1) { p.g.destroy(); continue; }
      p.vx *= DRAG;
      p.vy = p.vy * DRAG + GRAVITY * dt;
      p.g.x += p.vx;
      p.g.y += p.vy;
      p.g.rotation += p.vrot;
      // Fade out over the last 35% of life.
      p.g.alpha = t < 0.65 ? 1 : Math.max(0, 1 - (t - 0.65) / 0.35);
      kept.push(p);
    }
    this.pieces = kept;
  }

  /** Drop everything. Used on scene teardown. */
  clear(): void {
    for (const p of this.pieces) p.g.destroy();
    this.pieces = [];
  }
}
